import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      
      // Admin credentials (matching AdminLogin page)
      const adminCredentials = [
        { email: 'admin@sewa.com', password: 'admin123' },
        { email: 'manager@sewa.com', password: 'manager123' },
        { email: 'coordinator@sewa.com', password: 'coordinator123' },
        { email: 'r.deepanrajofficial@gmail.com', password: 'admin123' } // Legacy admin
      ];
      
      // Check for admin credentials first
      const isAdmin = adminCredentials.some(
        cred => cred.email === data.email && cred.password === data.password
      );
      
      if (isAdmin) {
        localStorage.setItem('adminAuth', 'true');
        navigate('/admin-dashboard');
        return;
      }
      
      // Check for student credentials
      if (data.email === 'student@demo.com' && data.password === 'student123') {
        // Store student authentication data
        const studentAuth = {
          user: {
            id: 'STU2025001',
            name: 'Demo Student',
            email: data.email,
            studentId: 'STU2025001',
            phone: '+91 98765 43210',
            course: 'B.Tech CSE',
            year: '2nd Year',
            college: 'Engineering College',
            city: 'Mumbai',
            state: 'Maharashtra'
          },
          token: 'demo-student-token-' + Date.now(),
          role: 'student'
        };
        
        localStorage.setItem('studentAuth', JSON.stringify(studentAuth));
        navigate('/student-dashboard');
        return;
      }
      
      // Regular user login
      const success = await login(data.email, data.password);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid student credentials. Try: student@demo.com / student123 for demo access.');
      }
    } catch {
      setError('An error occurred during login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated Neural Network Background */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <svg width="100%" height="100%" viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          <g>
            <circle cx="200" cy="200" r="6" fill="#38bdf8">
              <animate attributeName="r" values="6;12;6" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="600" cy="400" r="6" fill="#a78bfa">
              <animate attributeName="r" values="6;12;6" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="400" cy="300" r="8" fill="#f472b6">
              <animate attributeName="r" values="8;14;8" dur="2.5s" repeatCount="indefinite" />
            </circle>
            <line x1="200" y1="200" x2="400" y2="300" stroke="#38bdf8" strokeWidth="2">
              <animate attributeName="stroke-width" values="2;4;2" dur="2s" repeatCount="indefinite" />
            </line>
            <line x1="400" y1="300" x2="600" y2="400" stroke="#a78bfa" strokeWidth="2">
              <animate attributeName="stroke-width" values="2;4;2" dur="2s" repeatCount="indefinite" />
            </line>
            <line x1="200" y1="200" x2="600" y2="400" stroke="#f472b6" strokeWidth="2">
              <animate attributeName="stroke-width" values="2;4;2" dur="2s" repeatCount="indefinite" />
            </line>
          </g>
        </svg>
      </div>
      <div className="max-w-md w-full space-y-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="flex justify-center mb-4">
            <img src="/SEWA-logo.webp" alt="SEWA Logo" className="w-16 h-16 rounded-lg object-cover shadow-lg" loading="lazy" />
          </div>
          <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">Student Portal</h2>
          <p className="mt-2 text-cyan-200">Sign in to access your student dashboard</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-black/80 p-8 rounded-xl shadow-2xl border border-cyan-400/20 backdrop-blur-lg"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}


            <div>
              <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-1">
                Email Address or Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email or username is required',
                  })}
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="Enter your email or username"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-blue-200 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="block w-full pl-10 pr-10 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Forgot password?
              </Link>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 text-white py-2 px-4 rounded-lg hover:scale-105 focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold shadow-lg"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-cyan-200">
              Don't have an account?{' '}
              <Link to="/signup" className="text-pink-400 hover:text-purple-400 font-bold">
                Sign up here
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;